import React from 'react';
import styled from '@emotion/styled';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { useFeatureToggle, useApplicationContext } from '@css-xp/base';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { shouldShowACHPaymentMethod } from '@css-xp/ppc-utils';
import { CreateBillingProfilePaneV2 } from './create-billing-profile-pane-v2';
import { useGetRelatedDomainsForCurrentTxA } from '@css-xp/ppc-custom-hooks';
import {
  CommerceAddress,
  FetchTransactionAccountForCreateBillingProfileQuery,
  FetchTransactionAccountForCreateBillingProfileQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { fetchTransactionAccountForCreateBillingProfile } from '../queries/fetch-transaction-account-for-create-billing-profile';

const BillingProfileFormStyles = styled.div`
  height: 100%;
  overflow-y: scroll;
  .create-billing-profile-form {
    height: 100%;
  }
`;

interface Props {
  isOpen: boolean;
  transactionAccountId: string;
  onClose: () => void;
  customerAddresses?: CommerceAddress[];
  existingBillingProfileDetails?: any;
  refetchBillingProfiles?: () => Promise<ApolloQueryResult<unknown>>;
  handleAddNewAddressClick?: () => void;
  shouldRenderForInternalUse?: boolean;
  isCustomerContactsEditable?: boolean;
}

export const CreateBillingProfileModalV2 = ({
  isOpen,
  transactionAccountId,
  onClose,
  customerAddresses,
  existingBillingProfileDetails,
  refetchBillingProfiles,
  handleAddNewAddressClick,
  shouldRenderForInternalUse,
  isCustomerContactsEditable,
}: Props) => {
  const { data: transactionAccountData, loading: transactionAccountDataLoading } = useQuery<
    FetchTransactionAccountForCreateBillingProfileQuery,
    FetchTransactionAccountForCreateBillingProfileQueryVariables
  >(fetchTransactionAccountForCreateBillingProfile, { variables: { id: transactionAccountId } });

  const [isEnabled] = useFeatureToggle();
  const context = useApplicationContext();
  const selectedPartnerAccountData = context.partnerAccounts.find(
    ({ transactionAccountId: txaId }: { transactionAccountId: string }) => txaId === transactionAccountId
  );
  const isACHPaymentMethodEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ACH_DIRECT_DEBIT);

  const { relatedDomains, isLoading: relatedDomainsLoading } = useGetRelatedDomainsForCurrentTxA();

  return (
    <ModalTransition>
      {isOpen && !transactionAccountDataLoading && !relatedDomainsLoading && (
        <Modal onClose={onClose} testId="add-customer-modal" height="600px">
          <BillingProfileFormStyles>
            <CreateBillingProfilePaneV2
              transactionAccountId={transactionAccountId}
              transactionAccountDetails={transactionAccountData}
              soldToAddresses={customerAddresses}
              isTermPaymentSelectionDisabled={true}
              closeModal={onClose}
              existingBillingProfileDetails={existingBillingProfileDetails}
              refetch={refetchBillingProfiles}
              handleAddNewAddressClick={handleAddNewAddressClick}
              showAddNewAddressOption={true}
              shouldRenderForInternalUse={shouldRenderForInternalUse}
              shouldShowACHPaymentMethod={shouldShowACHPaymentMethod(
                selectedPartnerAccountData?.billTo?.country,
                isACHPaymentMethodEnabled
              )}
              validDomains={relatedDomains}
              isCustomerContactsEditable={isCustomerContactsEditable}
            />
          </BillingProfileFormStyles>
        </Modal>
      )}
    </ModalTransition>
  );
};
